/**
 * Exchange cards for the C Mode session dashboard (Doc 26; SPEC-01 §4.1).
 *
 * The dashboard's card stack is the Next-Step Engine's output rendered
 * consumer-grade (Doc 09 — no parallel engine): these helpers only *select and
 * order* the server-authoritative responsibility cards for display. They never
 * reclassify (Doc 06 — classification is server truth).
 */

import type { ResponsibilityCard } from '../../../types/prioritization/responsibility';
import { isResponsibilityValid } from '../../../types/prioritization/responsibility';

/** Plain product language for responsibility scopes (Doc 26 rule 5). */
export const SCOPE_PLAIN_LABELS: Record<string, string> = {
  CATCHBALL: 'Exchange',
  CYCLE: 'Cycle',
  TASK: 'To-do',
  SESSION: 'Session',
  PLANNING: 'Planning',
};

export interface ExchangeCardGroups {
  /** The ball is with you — one CTA each. */
  required: ResponsibilityCard[];
  /** The ball is with someone else — named waiting states, no CTA. */
  waiting: ResponsibilityCard[];
}

/**
 * Select the responsibility cards the C dashboard shows *in addition to* its
 * session cards. SESSION-scope rows are excluded (the dashboard already renders
 * richer session cards for those); NONE and invalid rows are absent, never
 * disabled (Doc 26 rule 2).
 */
export function selectExchangeCards(cards: ResponsibilityCard[]): ExchangeCardGroups {
  const eligible = cards.filter(
    c => c.scope !== 'SESSION' && isResponsibilityValid(c)
  );

  const byDeadline = (a: ResponsibilityCard, b: ResponsibilityCard) => {
    if (a.deadline && b.deadline) return a.deadline.localeCompare(b.deadline);
    if (a.deadline) return -1;
    if (b.deadline) return 1;
    return (a.created_at ?? '').localeCompare(b.created_at ?? '');
  };

  return {
    required: eligible.filter(c => c.classification === 'REQUIRED').sort(byDeadline),
    waiting: eligible.filter(c => c.classification === 'WAITING').sort(byDeadline),
  };
}

/**
 * The one-line ball statement for the dashboard header (Doc 26 rule 4 — the
 * ball is always named, on every screen).
 *
 * @param neededNow  count of things that need the viewer (sessions needing
 *                   input + REQUIRED exchange cards)
 * @param waiting    count of named waiting states (ball with someone else)
 */
export function ballSummaryLine(neededNow: number, waiting: number): string {
  if (neededNow > 0) {
    return `The ball is with you — ${neededNow} thing${neededNow === 1 ? '' : 's'} need${neededNow === 1 ? 's' : ''} you.`;
  }
  if (waiting > 0) {
    return `Nothing is needed from you — the ball is with others on ${waiting} exchange${waiting === 1 ? '' : 's'}. You'll hear when it moves.`;
  }
  return 'Nothing is needed from you right now.';
}

/** Short due-date phrasing for a card's deadline. */
export function deadlinePhrase(deadline: string | null | undefined): string | null {
  if (!deadline) return null;
  const due = new Date(deadline);
  if (Number.isNaN(due.getTime())) return null;
  const days = Math.floor((due.getTime() - Date.now()) / 86_400_000);
  if (days < 0) return 'Overdue';
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  if (days < 7) return `Due in ${days} days`;
  return `Due ${due.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
}
